/**
 * Entra ID OAuth Callback Page
 * Handles the OAuth callback from Microsoft Entra ID SSO
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import headerBg from '../assets/header-bg.svg';
import logo from '../assets/logo.svg';
import { useAuth } from '../contexts/AuthContext';
import { entraIDAuthService } from '../services/entraIdAuth';

const EntraIDCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleAuthSuccess, handleAuthError, clearError } = useAuth() as any;
  const [status, setStatus] = useState<'processing' | 'success' | 'error' | 'initializing'>(
    'processing', // Start with processing instead of initializing
  );
  const [message, setMessage] = useState('Processing Entra ID authentication...');
  const hasRun = useRef(false); // Prevent double execution in React StrictMode

  const getPostLoginRedirect = (): string => {
    const fallbackPath = '/';
    const storedPath = sessionStorage.getItem('post_login_redirect');
    sessionStorage.removeItem('post_login_redirect');

    if (!storedPath?.startsWith('/')) {
      return fallbackPath;
    }

    return storedPath;
  };

  useEffect(() => {
    // Prevent double execution (React StrictMode in dev can cause this)
    if (hasRun.current) {return;}
    hasRun.current = true;
    
    // Clear any existing errors from previous auth attempts
    clearError();
    handleOAuthCallback();
    // eslint-disable-next-line
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Small delay to ensure URL params are fully loaded
      await new Promise((resolve) => setTimeout(resolve, 100));
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        const errorMsg = errorDescription || error;
        throw new Error(`Entra ID OAuth error: ${errorMsg}`);
      }

      if (!code || !state) {
        throw new Error('Missing OAuth parameters (code or state)');
      }

      setMessage('Exchanging authorization code...');

      const authResponse = await entraIDAuthService.handleCallback(code, state);
      
      // Save auth data to localStorage and state
      handleAuthSuccess(authResponse);
      
      setStatus('success');
      setMessage('Authentication successful! Redirecting...');

      // Navigate in-app to preserve in-memory access token state.
      // Full page reload would clear the in-memory token and may trigger a
      // refresh-before-cookie race in some environments.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate(getPostLoginRedirect(), { replace: true });
    } catch (error) {
      setStatus('error');
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      setMessage(errorMessage);

      handleAuthError({ message: errorMessage });

      // Redirect to login after error
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-base-100 rounded-lg shadow-xl border-4 border-secondary overflow-hidden">
          {/* Shell Logo/Brand with TopBar styling */}
          <div
            className="w-full text-center border-b-4 border-red-600 flex items-center justify-center"
            style={{
              backgroundImage: `url(${headerBg})`,
              backgroundSize: '100% auto',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#FFF6D6',
              height: '68px',
              padding: '12px 2rem',
            }}
          >
            <h1
              className="text-2xl font-bold flex items-center gap-2"
              style={{
                color: '#ED1C24',
              }}
            >
              <img src={logo} alt="Archie Logo" className="w-8 h-8" />
              Archie
            </h1>
          </div>

          {/* Status Content */}
          <div className="p-8 text-center">
            {/* Status Icon */}
            <div className="mb-6">
              {(status === 'initializing' || status === 'processing' || status === 'success') && (
                <div className="flex justify-center">
                  <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-error/20 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-error"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
            {/* Status Message */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">
                {(status === 'initializing' || status === 'processing') &&
                  'Authenticating with Microsoft Entra ID...'}
                {status === 'success' && 'Welcome to Archie!'}
                {status === 'error' && 'Authentication Failed'}
              </h2>
              <p
                className={`text-sm ${
                  status === 'error' ? 'text-error' : 'text-base-content/70'
                }`}
              >
                {message}
              </p>
              {status === 'error' && (
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#336094] hover:bg-[#6e94c0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0097a9] transition-all duration-200 gap-2"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntraIDCallbackPage;
